/**
 * ADA patch: keyboard accessibility for the "Drop a Hint" app modal.
 *
 * Verified against the live product page (Drop a Hint 2.0 app embed). The app
 * injects <div id="dropahintModals"> as a direct child of <html> (NOT <body>),
 * holding two dialogs that it shows/hides with an inline display style:
 *
 *   #hint_modal          - the "drop a hint" form
 *   #hint_modal_closing  - the confirmation shown after sending
 *
 * Audit findings this patch addresses (app code is not editable, so the fix
 * lives here in the theme, same approach as ada-calendly-popup.js):
 *
 *   1. FOCUS IS NOT TRAPPED. The backdrop is only a CSS blur, which does
 *      nothing to the tab order - 113 focusable elements behind the modal
 *      stayed reachable while it was open. Keyboard users tab straight out of
 *      the dialog into blurred content they cannot read. (WCAG 2.4.3, Level A)
 *   2. THE SUBMIT BUTTON IS NOT KEYBOARD OPERABLE. "Send Hint to" is an
 *      <a> with NO href attribute, so it is not in the tab order at all - a
 *      keyboard user can fill the form but can never submit it.
 *      (WCAG 2.1.1 Keyboard, Level A)
 *   3. The dialog is not exposed as a dialog to assistive tech, and Escape
 *      does not close it.
 *
 * Everything below is ADDITIVE. We never preventDefault the app's own click
 * handlers, never move focus the app already placed, and never alter the
 * app's open/close logic - closing always routes through the app's own close
 * button so its internal state stays correct.
 */
(function () {
  'use strict';

  var MODALS_CONTAINER = 'dropahintModals';
  var MODAL_IDS = ['hint_modal', 'hint_modal_closing'];
  var CLOSE_SELECTOR = '.u-close-button';
  var SEND_SELECTOR = '#u_content_button_1_sendHint a';
  var TRIGGER_SELECTOR = '#dropahint-cta a';
  var FOCUSABLE =
    'a[href], button, input, textarea, select, iframe, ' +
    '[tabindex]:not([tabindex="-1"])';

  var LABELS = {
    hint_modal: 'Drop a hint',
    hint_modal_closing: 'Drop a hint confirmation'
  };

  var lastTrigger = null;
  var openModal = null; // the modal we are currently guarding, or null

  // Container test - display/visibility ONLY, no size check. The modal wrapper
  // is display:block with a bounding height of 0 (the visible panel inside it is
  // positioned), so a getBoundingClientRect test would wrongly report it closed.
  function isDisplayed(el) {
    if (!el) return false;
    var style = window.getComputedStyle(el);
    return style.display !== 'none' && style.visibility !== 'hidden';
  }

  // Control test - these DO have real box dimensions, so size is a valid way to
  // skip anything hidden or collapsed inside the dialog.
  function isVisible(el) {
    if (!el || !isDisplayed(el)) return false;
    var r = el.getBoundingClientRect();
    return r.width > 0 && r.height > 0;
  }

  // The app toggles an inline display style between none and block.
  function findOpenModal() {
    for (var i = 0; i < MODAL_IDS.length; i++) {
      var el = document.getElementById(MODAL_IDS[i]);
      if (el && isDisplayed(el)) return el;
    }
    return null;
  }

  function focusablesIn(modal) {
    var list = modal.querySelectorAll(FOCUSABLE);
    var out = [];
    for (var i = 0; i < list.length; i++) {
      if (isVisible(list[i]) && !list[i].disabled) out.push(list[i]);
    }
    return out;
  }

  /* ---------------------------------------------------------------- inert -- */

  function inertElement(el) {
    if (el.hasAttribute('data-ada-dah-inert')) return;
    el.setAttribute('data-ada-dah-inert', '');
    if (el.hasAttribute('aria-hidden')) {
      el.setAttribute('data-ada-dah-prev-ariahidden', el.getAttribute('aria-hidden'));
    }
    el.setAttribute('aria-hidden', 'true');
    try { el.inert = true; } catch (e) { /* older browsers */ }
  }

  // Hide every sibling at every level from the modal up to <html>, so the
  // dialog is the only interactive subtree. The app mounts its container on
  // <html>, so this correctly makes <body> (the whole store) inert.
  function setBackgroundInert(modal) {
    var node = modal;
    var root = document.documentElement;
    while (node && node !== root && node.parentElement) {
      var siblings = node.parentElement.children;
      for (var i = 0; i < siblings.length; i++) {
        if (siblings[i] !== node) inertElement(siblings[i]);
      }
      node = node.parentElement;
    }
  }

  function clearBackgroundInert() {
    var marked = document.querySelectorAll('[data-ada-dah-inert]');
    for (var i = 0; i < marked.length; i++) {
      var el = marked[i];
      el.removeAttribute('data-ada-dah-inert');
      try { el.inert = false; } catch (e) { /* ignore */ }
      if (el.hasAttribute('data-ada-dah-prev-ariahidden')) {
        el.setAttribute('aria-hidden', el.getAttribute('data-ada-dah-prev-ariahidden'));
        el.removeAttribute('data-ada-dah-prev-ariahidden');
      } else {
        el.removeAttribute('aria-hidden');
      }
    }
  }

  /* -------------------------------------------------------------- controls -- */

  // "Send Hint to" is an <a> with no href, so the browser will not focus it.
  // Give it button semantics and put it in the tab order. We only synthesise a
  // click on Enter/Space - the app's own click handler then runs untouched.
  function enhanceSendButton(modal) {
    var send = modal.querySelector(SEND_SELECTOR);
    if (!send || send.dataset.adaDahPatched) return;
    send.dataset.adaDahPatched = 'true';
    send.setAttribute('role', 'button');
    if (!send.hasAttribute('tabindex')) send.setAttribute('tabindex', '0');
    send.addEventListener('keydown', function (evt) {
      if (evt.key === 'Enter' || evt.key === ' ' || evt.key === 'Spacebar') {
        evt.preventDefault(); // stop Space scrolling; does not touch the app's click
        evt.currentTarget.click();
      }
    });
  }

  // The close control is already an <a href="#"> (focusable, Enter works
  // natively), so we only add the accessible name. No handler is attached -
  // the app's own close logic must stay the single source of truth.
  function enhanceCloseButton(modal) {
    var close = modal.querySelector(CLOSE_SELECTOR);
    if (!close || close.dataset.adaDahPatched) return;
    close.dataset.adaDahPatched = 'true';
    if (!close.getAttribute('aria-label')) {
      close.setAttribute('aria-label', close.getAttribute('title') || 'Close');
    }
    var icon = close.querySelector('.icon-cross');
    if (icon) icon.setAttribute('aria-hidden', 'true');
  }

  function markAsDialog(modal) {
    if (modal.getAttribute('role') !== 'dialog') {
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', LABELS[modal.id] || 'Dialog');
    }
    if (!modal.hasAttribute('tabindex')) modal.setAttribute('tabindex', '-1');
  }

  function closeModal(modal) {
    var close = modal.querySelector(CLOSE_SELECTOR);
    if (close) close.click(); // route through the app's own close handler
  }

  /* ----------------------------------------------------------------- state -- */

  // Single source of truth, re-run on every DOM change. Self-healing: whichever
  // way the modal ends up closed, the background is always released, so the
  // store can never be left inert.
  function syncState() {
    var modal = findOpenModal();

    if (modal && openModal !== modal) {
      if (openModal) clearBackgroundInert();
      openModal = modal;
      markAsDialog(modal);
      enhanceCloseButton(modal);
      enhanceSendButton(modal);
      setBackgroundInert(modal);

      // The app already focuses the first field on open - only step in if it
      // did not, so we never fight its behaviour.
      window.setTimeout(function () {
        if (findOpenModal() !== modal) return;
        if (!modal.contains(document.activeElement)) modal.focus();
      }, 50);
    } else if (modal && openModal === modal) {
      // Re-render while open (e.g. validation messages): keep controls patched.
      enhanceCloseButton(modal);
      enhanceSendButton(modal);
    } else if (!modal && openModal) {
      openModal = null;
      clearBackgroundInert();
      if (lastTrigger && document.body.contains(lastTrigger) && isVisible(lastTrigger)) {
        lastTrigger.focus();
      }
    }
  }

  // Collapse bursts of mutations (the app re-renders in batches) into a single
  // check per frame, so we never trigger a style recalc more than once a frame.
  var syncQueued = false;
  function scheduleSync() {
    if (syncQueued) return;
    syncQueued = true;
    window.requestAnimationFrame(function () {
      syncQueued = false;
      syncState();
    });
  }

  /* ------------------------------------------------------------------ init -- */

  function init() {
    // Remember which control opened the dialog so focus can return there.
    document.addEventListener(
      'click',
      function (evt) {
        if (!evt.target || !evt.target.closest) return;
        var trigger = evt.target.closest(TRIGGER_SELECTOR);
        if (trigger) lastTrigger = trigger;
      },
      true
    );

    var trigger = document.querySelector(TRIGGER_SELECTOR);
    if (trigger) trigger.setAttribute('aria-haspopup', 'dialog');

    // Attribute changes are watched ONLY inside the app's own container. The
    // theme animates Flickity carousels through inline styles, so observing
    // attributes document-wide would wake this patch on every animation frame.
    // The document is watched for childList only (far cheaper, and debounced),
    // so the container is still found wherever the app decides to mount it.
    var containerObserver = new MutationObserver(scheduleSync);

    function watchContainer() {
      var container = document.getElementById(MODALS_CONTAINER);
      if (!container || container.dataset.adaDahObserved) return;
      container.dataset.adaDahObserved = 'true';
      containerObserver.observe(container, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class']
      });
      scheduleSync();
    }

    new MutationObserver(function () {
      watchContainer();
      scheduleSync();
    }).observe(document.documentElement, { childList: true, subtree: true });

    watchContainer();
    syncState();

    // Focus trap: Tab / Shift+Tab cycle inside the dialog only.
    document.addEventListener(
      'keydown',
      function (evt) {
        var modal = findOpenModal();
        if (!modal) return;

        if (evt.key === 'Escape' || evt.key === 'Esc') {
          evt.preventDefault();
          closeModal(modal);
          return;
        }

        if (evt.key !== 'Tab') return;

        var f = focusablesIn(modal);
        if (!f.length) return;
        var first = f[0];
        var last = f[f.length - 1];
        var active = document.activeElement;

        if (!modal.contains(active)) {
          evt.preventDefault();
          first.focus();
        } else if (!evt.shiftKey && active === last) {
          evt.preventDefault();
          first.focus();
        } else if (evt.shiftKey && (active === first || active === modal)) {
          evt.preventDefault();
          last.focus();
        }
      },
      true
    );

    // Safety net: anything that still lands outside the dialog (browser chrome
    // round-trip, stray script focus) gets pulled back in.
    document.addEventListener('focusin', function (evt) {
      var modal = findOpenModal();
      if (!modal || modal.contains(evt.target)) return;
      var f = focusablesIn(modal);
      var target = f.length ? f[0] : modal;
      window.setTimeout(function () {
        if (findOpenModal() === modal) target.focus();
      }, 0);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
